import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Banner from "components/mis-survey/sections/banner/Banner";
import styles from "../mis-long-survey/survey.module.css";
import { IoIosArrowRoundForward } from "react-icons/io";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	conditionalQuizzes as conditionalQuizzesFn,
	generateQuizSchema,
	transformUserResponse,
} from "utils/helpers";
import toast from "react-hot-toast";
import {
	useGetSectionById,
	useGetSectionQuestions,
	useSubmitUATSurveyData,
	useValidateSurveyCode,
} from "api/hooks";
import RadioInputs from "components/ui/RadioInputBoxes";
import { Backdrop, CircularProgress } from "@mui/material";
import DropDownInput from "components/ui/DropDownInput";

const ACTSurvey = () => {
	const navigate = useNavigate();
	const [searchParam] = useSearchParams();
	const getQuestions = useGetSectionQuestions(9);
	const getCurrentSection = useGetSectionById(9);

	const agentNumber = searchParam.get("userId") ?? "";
	const surveyChannel = searchParam.get("channel") ?? "";
	const surveyCode = searchParam.get("c") ?? "";

	const [showForm, setShowForm] = useState(false);
	const [respondent, setRespondent] = useState();

	const { validateCode, isLoading: isValidating } = useValidateSurveyCode({
		onSuccess: (data) => {
			setRespondent(data?.data);
			setShowForm(true);
			toast.success("You have access to this survey");
		},
		onError: (error) => {
			const errorMessage = error?.response?.data?.message;
			toast.error(
				errorMessage || "You do not have access to this survey"
			);
		},
	});

	const [formKey, setFormKey] = useState(0);
	const initialConditionalQuizzes = conditionalQuizzesFn([]);

	const hiddenQuizzes = useRef(initialConditionalQuizzes);

	const formSchema = generateQuizSchema(
		getQuestions?.data?.data ?? [],
		hiddenQuizzes
	);

	const otherQuizText = {};

	const {
		control,
		handleSubmit,
		setValue,
		register,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(formSchema),
	});

	const { submitUATSurvey, isLoading } = useSubmitUATSurveyData({
		onSuccess: () => {
			toast.success("survey submitted successfully");
			setTimeout(() => {
				navigate("/ml-thank-you", { replace: true });
			}, 400);
		},
		onError: (error) => {
			const errorMessage = error?.response?.data?.message;
			if (Array.isArray(errorMessage) && errorMessage.length > 1) {
				toast.error("An error occured, please try again");
			} else {
				toast.error(
					errorMessage || "unable to submit survey, please try again"
				);
			}
		},
	});

	const onSubmit = (data) => {
		hiddenQuizzes.current.forEach((id) =>
			setValue(id.toString(), undefined)
		);
		transformUserResponse(data, otherQuizText, (responses) => {
			submitUATSurvey({
				survey_id: 2,
				channel: surveyChannel !== "" ? surveyChannel : "direct",
				agent: agentNumber !== "" ? Number(agentNumber) : null,
				survey_code: surveyCode,
				section_id: 9,
				respondent: {
					phone_number: respondent?.phone_number,
				},
				response: responses,
				is_survey_end: true,
			});
		});
	};

	const updateHiddenQuizes = (logicObj) => {
		const allQuestions = getQuestions.data.data;
		const currentQuizIndex = allQuestions.findIndex(
			(q) => q.quiz_id === logicObj?.currentId
		);
		const nextIndex = allQuestions.findIndex(
			(q) => q.quiz_id === logicObj?.targetedId
		);

		const lastQuizId = allQuestions?.[allQuestions?.length - 1]?.quiz_id;
		const lastQuizIndex = allQuestions.findIndex(
			(q) => q.quiz_id === lastQuizId
		);

		const quizesInbetween = allQuestions
			.slice(
				currentQuizIndex + 1,
				nextIndex === -1 ? lastQuizIndex + 1 : nextIndex
			)
			.map((q) => q.quiz_id);

		//REMOVE action indicates adding the quizes in between that should be skipped
		if (logicObj?.action === "REMOVE") {
			const newHiddenQuizzes = hiddenQuizzes.current?.filter(
				(id) => id !== logicObj?.targetedId
			);

			const updatedHiddenQuizes = [
				...new Set([...newHiddenQuizzes, ...quizesInbetween]),
			];
			hiddenQuizzes.current = updatedHiddenQuizes;
			setFormKey((prev) => prev + 1);
		}

		//ADD action indicates removing the previous hidden quizes in between
		if (logicObj?.action === "ADD") {
			const newHiddenQuizzes = hiddenQuizzes.current?.filter(
				(id) => !quizesInbetween?.includes(id)
			);
			const nestedChildQuizIds = [];

			const targetedQuiz = getQuestions?.data?.data?.find(
				(quiz) => quiz.quiz_id === logicObj?.targetedId
			);

			targetedQuiz?.options?.forEach((option) => {
				if (option?.next_question_id) {
					nestedChildQuizIds.push(option.next_question_id);
				}
			});

			const updatedHiddenQuizes = [
				...new Set([...newHiddenQuizzes, ...nestedChildQuizIds]),
			];

			hiddenQuizzes.current = updatedHiddenQuizes;
			setFormKey((prev) => prev + 1);
		}
	};

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
		validateCode({ survey_code: surveyCode ?? "", survey_id: 2 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<section className={styles.container}>
			<Banner
				title={getCurrentSection.data?.data?.[0]?.section_type ?? ""}
				description={
					getCurrentSection.data?.data?.[0]?.Introductory_message ??
					""
				}
			/>

			<Backdrop
				sx={(theme) => ({
					color: "#fff",
					zIndex: theme.zIndex.drawer + 3,
				})}
				open={getQuestions.isLoading || isValidating}
			>
				<CircularProgress color="inherit" />
				<p className="ml-5 font-light">Loading section</p>
			</Backdrop>
			{showForm && (
				<form
					onSubmit={handleSubmit(onSubmit)}
					data-testid="main_survey_form"
					key={formKey}
				>
					{getQuestions?.data?.data
						?.filter(
							(quiz) =>
								!hiddenQuizzes.current.includes(quiz?.quiz_id)
						)
						.map((quiz, i) => {
							return (
								<React.Fragment key={i + 1}>
									{
										<>
											{quiz.quiz_type ===
												"SINGLE_SELECT" &&
												quiz.form_element ===
													"RADIO" && (
													<Controller
														name={quiz.quiz_id.toString()}
														control={control}
														render={({ field }) => (
															<RadioInputs
																field={field}
																question={
																	quiz.quiz_text
																}
																{...(register(
																	quiz.quiz_id.toString()
																),
																{
																	required: true,
																})}
																quizOptions={
																	quiz.options
																}
																index={i}
																hasError={Boolean(
																	errors[
																		quiz
																			.quiz_id
																	]
																)}
																errorMessage={
																	errors?.[
																		quiz
																			.quiz_id
																	]
																		? "this question is required"
																		: ""
																}
																onLogicChange={(
																	logic
																) => {
																	updateHiddenQuizes(
																		logic
																	);
																}}
															/>
														)}
													/>
												)}
											{quiz.quiz_type ===
												"SINGLE_SELECT" &&
												quiz.form_element ===
													"DROP-DOWN" && (
													<Controller
														name={quiz.quiz_id.toString()}
														control={control}
														render={({ field }) => (
															<DropDownInput
																hideOthers={
																	true
																}
																field={field}
																question={
																	quiz.quiz_text
																}
																index={i}
																quizOptions={
																	quiz.options
																}
																{...(register(
																	quiz.quiz_id.toString()
																),
																{
																	required: true,
																})}
																hasError={Boolean(
																	errors[
																		quiz
																			.quiz_id
																	]
																)}
																errorMessage={
																	errors?.[
																		quiz
																			.quiz_id
																	]
																		? "this question is required"
																		: ""
																}
																onLogicChange={(
																	logic
																) => {
																	updateHiddenQuizes(
																		logic
																	);
																}}
																hasOthersError={(
																	hasError
																) => {
																	console.log(
																		hasError
																	);
																}}
																onOtherTextChange={({
																	id,
																	text,
																}) => {
																	console.log(
																		{
																			id,
																			text,
																		}
																	);
																}}
															/>
														)}
													/>
												)}
										</>
									}
								</React.Fragment>
							);
						})}

					<div className="!flex !flex-wrap !items-center !gap-2">
						<button type="submit" disabled={isLoading}>
							{isLoading ? "Submitting" : "Submit"}
							<IoIosArrowRoundForward color="white" size={24} />
						</button>
					</div>
				</form>
			)}
		</section>
	);
};

export default ACTSurvey;
